import React, { useState, useEffect } from 'react';
import { ChevronDown, Palette, Wrench, Sparkles } from 'lucide-react';
import LazyImage from './LazyImage';

const Hero = () => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  const backgroundImages = [
    '/images/renovations/D320A096-C8D0-4438-8841-B0A43369780D_20250717_103219.webp',
    '/images/wood/15244C0E-39CB-4C34-872E-633F6728A604_20260118_005950.webp',
    '/images/color/0a6a487b-2d29-4ad5-a495-39f5790ef374_20260118_005937.webp',
    '/images/renovations/IMG_0083_20260118_005958.webp'
  ];

  const imageAltTexts = [
    'שיפוץ רהיטים מקצועי - תמונה ראשית המציגה את עבודות האיכות של מומחי הצביעה',
    'עבודות עץ איכותיות - צביעה וגימור מקצועי לרהיטי עץ',
    'צביעה בצבע איכותי - תוצאות מושלמות עם חומרי איכות',
    'שיפוץ מקצועי - תמונה נוספת המדגימה את רמת העבודה הגבוהה'
  ];

  useEffect(() => {
    setIsVisible(true);
    
    // Preload only the first image immediately, others later
    const firstImg = new Image();
    firstImg.src = backgroundImages[0];
    
    // Preload remaining images after a short delay
    setTimeout(() => {
      backgroundImages.slice(1).forEach((src) => {
        const img = new Image();
        img.src = src;
      });
    }, 1000);
    
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % backgroundImages.length);
    }, 6000);

    return () => {
      clearInterval(interval);
    };
  }, [backgroundImages.length]);

  const scrollToGallery = () => {
    const gallerySection = document.querySelector('#gallery');
    if (gallerySection) {
      gallerySection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const scrollToContact = () => {
    const contactSection = document.querySelector('#contact');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="relative min-h-screen h-screen flex items-center justify-center overflow-hidden">
      {/* Animated Background Images */}
      {backgroundImages.map((image, index) => (
        <div
          key={index}
          className={`absolute inset-0 transition-opacity duration-2000 ${
            index === currentImageIndex ? 'opacity-100' : 'opacity-0'
          }`}
        >
          {index === 0 ? (
            // Critical first image - no lazy loading wrapper
            <img
              src={image}
              alt={imageAltTexts[0]}
              className="w-full h-full object-cover object-center"
              loading="eager"
              fetchPriority="high"
              decoding="sync"
              style={{ minHeight: '100vh', minWidth: '100vw' }}
            />
          ) : (
            // Non-critical images can use LazyImage
            <LazyImage
              src={image}
              alt={imageAltTexts[index]}
              className="w-full h-full object-cover object-center"
              loading="lazy"
              fetchPriority="low"
              aspectRatio=""
              style={{ minHeight: '100vh', minWidth: '100vw' }}
            />
          )}
        </div>
      ))}
      
      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-black/60 via-black/40 to-black/60"></div>
      
      {/* Animated Particles */}
      <div className="absolute inset-0 overflow-hidden">
        {[...Array(6)].map((_, i) => (
          <div
            key={i}
            className="absolute w-2 h-2 bg-amber-400/30 rounded-full animate-pulse"
            style={{
              left: `${20 + (i * 15)}%`,
              top: `${30 + (i * 10)}%`,
              animationDelay: `${i * 0.5}s`,
              animationDuration: `${3 + (i % 3)}s`
            }}
          />
        ))}
      </div>
      
      {/* Content */}
      <div className={`relative z-10 text-center px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto transition-all duration-1500 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
      }`}>
        
        {/* Main Title */}
        <div className="mb-8">
          <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold text-white mb-4 leading-tight">
            <span className="bg-gradient-to-r from-amber-300 to-amber-600 bg-clip-text text-transparent">
              מצבעת
            </span>
            <br />
            <span className="text-white drop-shadow-2xl">המומחים</span>
          </h1>
          <div className="w-32 h-1 bg-gradient-to-r from-amber-400 to-amber-600 mx-auto mb-6 rounded-full"></div>
        </div>

        {/* Features Row */}
        <div className="flex flex-wrap justify-center gap-6 mb-8 text-white/90">
          <div className="flex items-center space-x-2 space-x-reverse bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
            <Palette className="w-5 h-5 text-amber-400" />
            <span className="font-medium">צביעה מקצועית</span>
          </div>
          <div className="flex items-center space-x-2 space-x-reverse bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
            <Wrench className="w-5 h-5 text-amber-400" />
            <span className="font-medium">עבודות עץ</span>
          </div>
          <div className="flex items-center space-x-2 space-x-reverse bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
            <Sparkles className="w-5 h-5 text-amber-400" />
            <span className="font-medium">חידושים</span>
          </div>
        </div>
        
        {/* Description */}
        <p className="text-xl md:text-2xl lg:text-3xl text-white/95 mb-12 leading-relaxed max-w-4xl mx-auto font-light">
          אנו מתמחים בצביעת רהיטים בהתאמה אישית, עבודות עץ מקצועיות, ושדרוגים מתקדמים
          <br className="hidden md:block" />
          <span className="text-amber-300 font-medium">אצלנו הרהיטים מקבלים חיים חדשים!</span>
        </p>
        
        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
          <button 
            onClick={scrollToGallery}
            className="group bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white px-10 py-4 rounded-full font-bold text-lg transition-all duration-300 transform hover:scale-105 shadow-2xl hover:shadow-amber-500/25 min-w-[200px]"
            aria-label="גלול לגלריה לצפייה בעבודות שלנו"
          >
            <span className="flex items-center justify-center space-x-2 space-x-reverse">
              <span>צפה בעבודות שלנו</span>
              <ChevronDown className="w-5 h-5 group-hover:animate-bounce" />
            </span>
          </button>
          <button 
            onClick={scrollToContact}
            className="group bg-transparent border-3 border-white/80 text-white hover:bg-white hover:text-amber-800 px-10 py-4 rounded-full font-bold text-lg transition-all duration-300 transform hover:scale-105 backdrop-blur-sm min-w-[200px]"
            aria-label="גלול לקטע צור קשר"
          >
            צור קשר
          </button>
        </div>
      </div>
      
      {/* Background Image Indicators */}
      <div className="absolute bottom-8 right-8 flex space-x-2 space-x-reverse">
        {backgroundImages.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentImageIndex(index)}
            className={`w-3 h-3 rounded-full transition-all duration-300 ${
              index === currentImageIndex 
                ? 'bg-amber-400 scale-125' 
                : 'bg-white/50 hover:bg-white/80'
            }`}
            aria-label={`עבור לתמונת רקע ${index + 1}`}
            aria-pressed={index === currentImageIndex}
          />
        ))}
      </div>
    </section>
  );
};

export default Hero;